import { SpanViewReadings } from './models/sensor-by-id';
import { LibChart, LibChartData } from './models/lib-chart-model';
import * as moment from 'moment';
import { StringUtility } from '@app/shared/string-utility';
import { CommonUnits, SpanViewGranularityRate } from '@app/shared/enums';
import { ExtendedDateFormat, TwentyFourHourFormat, ISODateTimeStartOfDayFormat, DotDayMonthYearFormat, ISODateTimeFormat } from '@app/shared/constants';
import { range, sortBy } from 'lodash';
import { DateUtilities } from '@app/shared/date-utility';
import { SensorReadingState } from './models/sensor-reading-state';

export class SensorDataUtility {
  public static generateChartDetails(readings: SpanViewReadings[], state: SensorReadingState): LibChart {
    switch (state.granularity.trim().toLowerCase()) {
      case SpanViewGranularityRate.min15:
        readings = this.parseFifteenMinute(readings);
        return this.chartObj(readings, state);

      case SpanViewGranularityRate.hourly:
        readings = this.parseHourly(readings);
        return this.chartObj(readings, state);

      case SpanViewGranularityRate.daily:
        return this.chartObj(readings, state);

      case SpanViewGranularityRate.monthly:
        readings = this.parseMonthly(readings);
        return this.chartObj(readings, state);

      case SpanViewGranularityRate.yearly:
        readings = this.parseYearly(readings, state);
        return this.chartObj(readings, state);

      default:
        const emptyChart: LibChart = {} as LibChart;
        return emptyChart;
    }
  }

  private static chartValue(value: string, state: SensorReadingState): number | null {
    if (StringUtility.areStringsIdentical(state.granularity, SpanViewGranularityRate.yearly)) {
      return value ? parseFloat(JSON.parse(value)[0].Value) : null;
    }

    return value ? parseFloat(JSON.parse(value)[0].Value) : 0;
  }

  private static chartTooltip(reading: SpanViewReadings, state: SensorReadingState): string {
    switch (state.granularity.trim().toLowerCase()) {
      case SpanViewGranularityRate.min15:
      case SpanViewGranularityRate.hourly:
        return `${DateUtilities.toExpectedFormat(reading.date, DotDayMonthYearFormat)} - ${DateUtilities.toExpectedFormat(reading.date, TwentyFourHourFormat)}`;
      case SpanViewGranularityRate.daily:
        return DateUtilities.toExpectedFormat(reading.date, DotDayMonthYearFormat);
      case SpanViewGranularityRate.monthly:
        return DateUtilities.toExpectedFormat(reading.date, 'MMMM');
      case SpanViewGranularityRate.yearly:
        return DateUtilities.toExpectedFormat(reading.date, 'YYYY');
      default:
        return '';
    }
  }

  private static chartData(readings: SpanViewReadings[], state: SensorReadingState): LibChartData[] {
    return readings.map((reading: SpanViewReadings) => ({
      label: new Date(reading.date),
      value: this.chartValue(reading.returned_types, state),
      tooltip: this.chartTooltip(reading, state),
    }));
  }

  private static chartObj(readings: SpanViewReadings[], state: SensorReadingState): LibChart {
    const chartData: LibChartData[] = this.chartData(readings, state);
    const unit = StringUtility.areStringsIdentical(state.measurementTypeUnit, CommonUnits.count) ? CommonUnits.anzahl : state.measurementTypeUnit;
    return {
      data: chartData,
      dataLabel: `(${unit})`,
      dataLegend: '',
      dataUnit: unit,
    };
  }

  private static parseFifteenMinute(readings: SpanViewReadings[]): SpanViewReadings[] {
    const newReadings: SpanViewReadings[] = [] as SpanViewReadings[];
    const dateSet: Set<string> = this.dateSet(readings);

    dateSet.forEach((date: string) => {
      for (let index = 0; index <= 95; index++) {
        const dateString = moment(date)
          .minute(index * 15)
          .format(ISODateTimeFormat);
        this.generateNewReadings(readings, newReadings, dateString);
      }
    });

    return this.sortedReadings(newReadings);
  }

  private static parseHourly(readings: SpanViewReadings[]): SpanViewReadings[] {
    const newReadings: SpanViewReadings[] = [] as SpanViewReadings[];
    const dateSet: Set<string> = this.dateSet(readings);

    dateSet.forEach((date: string) => {
      for (let hour = 0; hour <= 23; hour++) {
        const dateString = moment(date).hour(hour).format(ISODateTimeFormat);
        this.generateNewReadings(readings, newReadings, dateString);
      }
    });

    return this.sortedReadings(newReadings);
  }

  private static parseMonthly(readings: SpanViewReadings[]): SpanViewReadings[] {
    const newReadings: SpanViewReadings[] = [] as SpanViewReadings[];
    const dateSet: Set<string> = this.dateSet(readings);
    const dateSetArray = Array.from(dateSet);
    const uniqueYears: Set<string> = new Set(dateSetArray.map((item: string) => moment(item).format('YYYY')));

    uniqueYears.forEach((date: string) => {
      for (let month = 0; month <= 11; month++) {
        const dateString = moment(date).month(month).format(ISODateTimeStartOfDayFormat);
        this.generateNewReadings(readings, newReadings, dateString);
      }
    });

    return this.sortedReadings(newReadings);
  }

  private static parseYearly(readings: SpanViewReadings[], state: SensorReadingState): SpanViewReadings[] {
    const newReadings: SpanViewReadings[] = [] as SpanViewReadings[];
    const startDateYear = moment(state.startDate).year();
    const endDateYear = moment(state.endDate).year();
    const yearRange = range(startDateYear, endDateYear + 1);

    // todo Franz :: convert to yearRange.at(-1)
    for (let startYear = startDateYear; startYear <= yearRange[yearRange.length - 1]; startYear++) {
      const dateString = moment(startYear).year(startYear).format(ISODateTimeStartOfDayFormat);
      this.generateNewReadings(readings, newReadings, dateString);
    }

    return this.sortedReadings(newReadings);
  }

  private static dateSet(readings: SpanViewReadings[]): Set<string> {
    return new Set(readings.map((reading: SpanViewReadings) => moment(reading.date).format(ExtendedDateFormat)));
  }

  private static generateNewReadings(readings: SpanViewReadings[], newReadings: SpanViewReadings[], dateString: string): void {
    const foundObject = readings.find((reading: SpanViewReadings) => StringUtility.areStringsIdentical(reading.date, dateString));

    if (!foundObject) {
      newReadings.push({
        date: dateString,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        returned_types: '',
      } as SpanViewReadings);
    } else {
      newReadings.push(foundObject);
    }
  }

  private static sortedReadings(newReadings: SpanViewReadings[]): SpanViewReadings[] {
    return sortBy(newReadings, (reading: SpanViewReadings) => reading.date);
  }
}
